/**
 * Consultas e gravações de Outros Bancos, das importações, da calculadora, da
 * recorrência e dos documentos pelo TanStack Query.
 *
 * Papel: aqui ficam as consultas da configuração da casa, do mapa CPF→nome, da
 * folha XLSX, da aba Outros Bancos, do recibo e da ordem; a leitura do último
 * grupo para "Repetir último lançamento"; e as mutações das rotas novas.
 *
 * Como usar: toda chave começa por `CHAVE_FINANCAS`, e **toda gravação invalida
 * `CHAVE_FINANCAS` inteira**, como as do caixa: a folha importada muda o
 * Fechamento, a calculadora muda a listagem e o `emUso` do favorecido, e a
 * inclusão de colaborador muda a lista de Cadastros. A recorrência é a exceção
 * que confirma a regra: ela só invalida quando gerou ou corrigiu algum grupo,
 * para a abertura de Lançamentos que não gerou nada não pedir tudo de novo.
 *
 * O mapa CPF→nome só é pedido com `habilitado`, porque a rota recusa quem só
 * tem `ver` e a consulta parada não pede o que não vai usar.
 *
 * As cinco gravações de um cartão de Outros Bancos — salário, dias, guia,
 * remoção da guia e fechamento — levam a mesma chave de mutação, que
 * `useCartaoGravando` conta. Uma gravação nova do cartão que fique sem ela não
 * desabilita "Fechar Folha" enquanto grava, e o fechamento volta a correr com
 * ela.
 */
import {
  type CalculadoraPetrella,
  type ConsultaDoMes,
  type ConsultaDoRecibo,
  type DiasDeOutrosBancos,
  type GuiaDaeDeOutrosBancos,
  type ImportacaoDaFolha,
  type ImportacaoDaGuiaDae,
  type MesDoFavorecido,
  type NovaFolhaImportada,
  type NovoLancamentoDeOutrosBancos,
  type Recorrencia,
  type SalarioDeOutrosBancos,
} from '@concierge/shared';
import {
  skipToken,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  buscarConfiguracao,
  buscarFolhaImportada,
  buscarNomesDaFolha,
  buscarOrdemDePagamento,
  buscarOutrosBancos,
  buscarRecibo,
  buscarUltimoGrupo,
  criarLancamentoDeOutrosBancos,
  criarPelaCalculadora,
  excluirLancamentoDeOutrosBancos,
  fecharFolha,
  gravarDias,
  gravarFolhaImportada,
  gravarGuiaDeOutrosBancos,
  gravarSalario,
  importarFolha,
  importarGuiaDae,
  incluirColaborador,
  removerFolhaImportada,
  removerFolhasFechadas,
  removerGuiaDeOutrosBancos,
  verificarRecorrencia,
} from '../api/financas-extras';
import { CHAVE_FINANCAS } from './usar-financas';

/**
 * Lê a configuração de Finanças da casa.
 *
 * @param residenciaId - A casa do caixa; nula deixa a consulta parada.
 * @returns A consulta da configuração.
 */
export function useConfiguracao(residenciaId: string | null) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'configuracao', residenciaId],
    queryFn:
      residenciaId === null
        ? skipToken
        : ({ signal }) => buscarConfiguracao(residenciaId, signal),
  });
}

/**
 * Lê o mapa CPF→nome da casa.
 *
 * @param residenciaId - A casa do caixa; nula deixa a consulta parada.
 * @param habilitado - Falso para quem só tem `ver`, que a rota recusaria.
 * @returns A consulta do mapa.
 */
export function useNomesDaFolha(residenciaId: string | null, habilitado: boolean) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'nomes-da-folha', residenciaId],
    queryFn:
      residenciaId === null || !habilitado
        ? skipToken
        : ({ signal }) => buscarNomesDaFolha(residenciaId, signal),
  });
}

/**
 * Lê a folha XLSX do mês aberto.
 *
 * @param consulta - A casa e o mês aberto; nula deixa a consulta parada.
 * @returns A consulta da folha, com `null` quando o mês não tem folha.
 */
export function useFolhaImportada(consulta: ConsultaDoMes | null) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'folha-importada', consulta],
    queryFn:
      consulta === null ? skipToken : ({ signal }) => buscarFolhaImportada(consulta, signal),
  });
}

/**
 * Lê a aba Outros Bancos do mês aberto.
 *
 * É a mesma consulta que os cartões "Folha de Pagamento" e "Lançamentos de
 * Outros Pagamentos" do Fechamento leem, e o TanStack Query faz uma só.
 *
 * @param consulta - A casa e o mês aberto; nula deixa a consulta parada.
 * @returns A consulta de Outros Bancos.
 */
export function useOutrosBancos(consulta: ConsultaDoMes | null) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'outros-bancos', consulta],
    queryFn:
      consulta === null ? skipToken : ({ signal }) => buscarOutrosBancos(consulta, signal),
  });
}

/**
 * Lê o recibo da página de documento.
 *
 * Não tenta de novo na falha: o 404 do lançamento que não existe é resposta, e a
 * página mostra a frase dele na hora.
 *
 * @param consulta - O lançamento, ou o favorecido e a competência.
 * @returns A consulta do recibo.
 */
export function useRecibo(consulta: ConsultaDoRecibo) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'recibo', consulta],
    queryFn: ({ signal }) => buscarRecibo(consulta, signal),
    retry: false,
  });
}

/**
 * Lê a ordem de pagamento da página de documento.
 *
 * @param mes - O favorecido e a competência.
 * @returns A consulta da ordem.
 */
export function useOrdemDePagamento(mes: MesDoFavorecido) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'ordem-de-pagamento', mes],
    queryFn: ({ signal }) => buscarOrdemDePagamento(mes, signal),
    retry: false,
  });
}

/**
 * Devolve a leitura do último grupo de um favorecido, sem guardá-la no cache.
 *
 * Quem chama é "Repetir último lançamento", no clique: o grupo tem de ser o que
 * está gravado agora, e não o de uma leitura anterior.
 *
 * @returns A função que lê o grupo, ou `null` quando o favorecido não tem grupo.
 */
export function useBuscarUltimoGrupo() {
  const clienteDeConsultas = useQueryClient();
  return (favorecidoId: string) =>
    clienteDeConsultas.fetchQuery({
      queryKey: [...CHAVE_FINANCAS, 'ultimo-grupo', favorecidoId],
      queryFn: ({ signal }) => buscarUltimoGrupo(favorecidoId, signal),
      staleTime: 0,
    });
}

/**
 * A chave de mutação das cinco gravações de um cartão de Outros Bancos, que
 * `useCartaoGravando` conta.
 *
 * É montada na chamada, como as chaves das consultas, e não na carga do
 * arquivo: os testes que substituem `./usar-financas` sem `CHAVE_FINANCAS`
 * quebrariam ao importar este arquivo.
 *
 * @returns `CHAVE_FINANCAS` seguida de `cartao-de-outros-bancos`.
 */
function chaveDasGravacoesDoCartao() {
  return [...CHAVE_FINANCAS, 'cartao-de-outros-bancos'];
}

/**
 * Diz se as variáveis de uma gravação são do cartão dado.
 *
 * @param variaveis - O que a gravação recebeu: `{ mes, corpo }` no salário, nos
 *   dias e na guia; o próprio `mes` na remoção da guia e no fechamento.
 * @param cartao - O favorecido e o mês do cartão.
 * @returns Verdadeiro quando o favorecido e a competência são os do cartão.
 */
function eDoCartao(variaveis: unknown, cartao: MesDoFavorecido): boolean {
  const mes =
    typeof variaveis === 'object' && variaveis !== null && 'mes' in variaveis
      ? variaveis.mes
      : variaveis;
  return (
    typeof mes === 'object' &&
    mes !== null &&
    'favorecidoId' in mes &&
    mes.favorecidoId === cartao.favorecidoId &&
    'competencia' in mes &&
    mes.competencia === cartao.competencia
  );
}

/**
 * Diz se alguma gravação de um cartão de Outros Bancos está pendente.
 *
 * Quem chama é o `CartaoDeFolha`, para desabilitar o que decide pela situação
 * do cartão enquanto o salário, os dias, a guia importada ou removida ou o
 * próprio fechamento não voltaram. Conta também a gravação que saiu de outro
 * componente, como as caixas de conferência do salário e da guia, que têm as
 * suas próprias mutações.
 *
 * A gravação só deixa de contar depois da nova busca do `onSettled`, porque o
 * TanStack Query só a dá por terminada depois dele: o clique seguinte já vê a
 * situação nova.
 *
 * @param cartao - O favorecido e o mês do cartão.
 * @returns Verdadeiro enquanto alguma gravação desse cartão não terminou.
 */
export function useCartaoGravando(cartao: MesDoFavorecido): boolean {
  return (
    useIsMutating({
      mutationKey: chaveDasGravacoesDoCartao(),
      predicate: (mutacao) => eDoCartao(mutacao.state.variables, cartao),
    }) > 0
  );
}

/**
 * Devolve a função que invalida todo o caixa depois de uma gravação.
 *
 * @returns A função, para o `onSettled` das mutações: a recusa da API também
 *   invalida, porque costuma dizer que outra pessoa mudou o caixa — pagou ou
 *   excluiu o lançamento — e a tela mostra o que já não existe. A promessa é
 *   devolvida, e o `mutateAsync` só termina depois da nova busca.
 */
function useInvalidarCaixa() {
  const clienteDeConsultas = useQueryClient();
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_FINANCAS });
}

/**
 * Grava a conferência da folha em PDF.
 *
 * @returns A mutação, que devolve quantos lançamentos nasceram.
 */
export function useImportarFolha() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: ImportacaoDaFolha) => importarFolha(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava a conferência da guia DAE de Lançamentos.
 *
 * @returns A mutação, que devolve quantos lançamentos nasceram.
 */
export function useImportarGuiaDae() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: ImportacaoDaGuiaDae) => importarGuiaDae(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Substitui a folha XLSX do mês.
 *
 * @returns A mutação.
 */
export function useGravarFolhaImportada() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: NovaFolhaImportada) => gravarFolhaImportada(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Remove a folha XLSX do mês.
 *
 * @returns A mutação, que recebe a casa e o mês.
 */
export function useRemoverFolhaImportada() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (consulta: ConsultaDoMes) => removerFolhaImportada(consulta),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava o que uma aba da calculadora pede.
 *
 * @returns A mutação, que devolve quantos lançamentos nasceram.
 */
export function useCriarPelaCalculadora() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: CalculadoraPetrella) => criarPelaCalculadora(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Pede a recorrência automática.
 *
 * @returns A mutação, que devolve se rodou e o que gerou; só invalida o caixa
 *   quando gerou ou corrigiu algum grupo.
 */
export function useVerificarRecorrencia() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: Recorrencia) => verificarRecorrencia(corpo),
    onSuccess: (resultado) => {
      if (resultado.criados > 0 || resultado.corrigidos > 0) void invalidar();
    },
  });
}

/**
 * Inclui um favorecido na folha de Outros Bancos.
 *
 * @returns A mutação, que recebe o identificador do favorecido.
 */
export function useIncluirColaborador() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (favorecidoId: string) => incluirColaborador(favorecidoId),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava o salário líquido de um cartão.
 *
 * @returns A mutação, que recebe o cartão e o corpo.
 */
export function useGravarSalario() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationKey: chaveDasGravacoesDoCartao(),
    mutationFn: ({ mes, corpo }: { mes: MesDoFavorecido; corpo: SalarioDeOutrosBancos }) =>
      gravarSalario(mes, corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava os dias de um cartão.
 *
 * @returns A mutação, que recebe o cartão e os dois dias.
 */
export function useGravarDias() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationKey: chaveDasGravacoesDoCartao(),
    mutationFn: ({ mes, corpo }: { mes: MesDoFavorecido; corpo: DiasDeOutrosBancos }) =>
      gravarDias(mes, corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Substitui a guia DAE de um cartão.
 *
 * @returns A mutação, que recebe o cartão e a guia.
 */
export function useGravarGuiaDeOutrosBancos() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationKey: chaveDasGravacoesDoCartao(),
    mutationFn: ({ mes, corpo }: { mes: MesDoFavorecido; corpo: GuiaDaeDeOutrosBancos }) =>
      gravarGuiaDeOutrosBancos(mes, corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Remove a guia DAE de um cartão.
 *
 * @returns A mutação, que recebe o cartão.
 */
export function useRemoverGuiaDeOutrosBancos() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationKey: chaveDasGravacoesDoCartao(),
    mutationFn: (mes: MesDoFavorecido) => removerGuiaDeOutrosBancos(mes),
    onSettled: () => invalidar(),
  });
}

/**
 * Fecha a folha de um cartão.
 *
 * @returns A mutação, que recebe o cartão e devolve a folha fechada.
 */
export function useFecharFolha() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationKey: chaveDasGravacoesDoCartao(),
    mutationFn: (mes: MesDoFavorecido) => fecharFolha(mes),
    onSettled: () => invalidar(),
  });
}

/**
 * Remove as folhas fechadas do mês.
 *
 * @returns A mutação, que recebe a casa e o mês.
 */
export function useRemoverFolhasFechadas() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (consulta: ConsultaDoMes) => removerFolhasFechadas(consulta),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava um lançamento de outros bancos.
 *
 * @returns A mutação, que recebe o formulário.
 */
export function useCriarLancamentoDeOutrosBancos() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: NovoLancamentoDeOutrosBancos) => criarLancamentoDeOutrosBancos(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Exclui um lançamento de outros bancos.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirLancamentoDeOutrosBancos() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (id: string) => excluirLancamentoDeOutrosBancos(id),
    onSettled: () => invalidar(),
  });
}
